import {ElementRef, inject, Injectable} from '@angular/core';
import {ErrorService} from "../../../../core/services/error.service";
import {
  allowedTypes,
  defaultFileValidationResult,
  maxSizeBytes
} from "../configs/telegram-chat-window-config";
import {FileValidationResult} from "../types/telegram-chat-window.types";
import {
  SendDocumentQueryParams,
  SendImageQueryParams,
  SendVideoQueryParams,
  UploadFileParams
} from "../types/telegram-page.types";
import {TelegramService} from "./telegram.service";
import {FilesService} from "../../../../core/services/files.service";
import {TelegramApiService} from "./telegram-api.service";
import {HttpEventType} from "@angular/common/http";
import {UploadProgressService} from "../../../../core/services/upload-progress.service";
import {ProgressStatus} from "../../../../core/types/upload-progress.types";

@Injectable({
  providedIn: 'root'
})
export class TelegramChatWindowService {
  private errorService: ErrorService = inject(ErrorService);
  private telegramService: TelegramService = inject(TelegramService);
  private filesService: FilesService = inject(FilesService);
  private telegramApiService: TelegramApiService = inject(TelegramApiService);
  private uploadProgressService: UploadProgressService = inject(UploadProgressService);

  public needUploadFileComponent: boolean = false;

  public async uploadFile({selectedFile, fileType, sendFileQueryParams }: UploadFileParams): Promise<void> {
    if (!selectedFile) { return; }

    const fileData = this.filesService.createFormData({
      file: selectedFile,
      fileType: fileType
    });

    this.needUploadFileComponent = true;
    switch (fileType) {
      case 'image':
        await this.sendImgMessage({
          ...sendFileQueryParams,
          image: fileData
        });
        break;
      case 'video':
        await this.sendVideoMessage({
          ...sendFileQueryParams,
          video: fileData
        });
        break;
      case 'document':
        await this.sendDocumentMessage({
          ...sendFileQueryParams,
          document: fileData
        });
        break;
    }

    this.needUploadFileComponent = false;
  }

  public validateFile(file: File): FileValidationResult {
    const fileValidationResult: FileValidationResult = defaultFileValidationResult;
    if (file.size > maxSizeBytes) {
      this.errorService.showErrorMessage(
        'Превышен максимальный размер файла. Максимальный размер: 1 ГБ.'
      );

      return fileValidationResult;
    }

    const fileType = file.type.toLowerCase();
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
    const isImage = allowedTypes.image.mime.includes(fileType) &&
      allowedTypes.image.extensions.includes(fileExtension);
    const isVideo = allowedTypes.video.mime.includes(fileType) &&
      allowedTypes.video.extensions.includes(fileExtension);
    const isDocument = allowedTypes.document.mime.includes(fileType) &&
      allowedTypes.document.extensions.includes(fileExtension);

    if (isImage) {
      fileValidationResult.type = 'image';
      fileValidationResult.isValid = true;
      return fileValidationResult;
    }

    if (isVideo) {
      fileValidationResult.type = 'video';
      fileValidationResult.isValid = true;
      return fileValidationResult;
    }

    if (isDocument) {
      fileValidationResult.type = 'document';
      fileValidationResult.isValid = true;
      return fileValidationResult;
    }

    this.errorService.showErrorMessage(
      'Недопустимый формат файла.'
    );

    return fileValidationResult;
  }

  public scrollToBottom(chatContainer: ElementRef): void {
    const container = chatContainer.nativeElement;
    if (Math.abs(container.scrollHeight - container.scrollTop - container.clientHeight) > 10) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    }
  }

  private async sendImgMessage(sendImageQueryParams: SendImageQueryParams): Promise<void> {
    this.telegramService.sendImageMessage(sendImageQueryParams).catch( error => {
      this.errorService.handleError(error);
    });
  }

  private async sendVideoMessage(sendVideoQueryParams: SendVideoQueryParams): Promise<void> {
    return new Promise((resolve, reject) => {
      this.telegramApiService.sendVideoMessage(sendVideoQueryParams).subscribe({
        next: (event) => {
          const progress = this.handleUploadEvent(event);
          this.uploadProgressService.updateProgress({
            progress: progress.progress,
            completed: progress.completed,
            fileName: sendVideoQueryParams.fileName,
            fileType: 'video',
          });
        },
        error: (error) => {
          this.errorService.handleError(error);
          this.uploadProgressService.resetProgress();
          reject(error);
        },
        complete: () => {
          setTimeout(() => {
            this.uploadProgressService.resetProgress();
            resolve();
          }, 1000);
        },
      });
    });
  }

  private async sendDocumentMessage(sendDocumentQueryParams: SendDocumentQueryParams): Promise<void> {
    this.telegramService.sendDocumentMessage(sendDocumentQueryParams).catch( error => {
      this.errorService.handleError(error);
    });
  }

  public handleUploadEvent(event: any): ProgressStatus {
    switch (event.type) {
      case HttpEventType.UploadProgress:
        const progress = Math.round(100 * event.loaded / (event.total || 1));
        return { progress, completed: false };
      case HttpEventType.Response:
        return { progress: 100, completed: true };
      default:
        return { progress: 0, completed: false };
    }
  }
}
